<template>
  <div class="container-fluid py-4 tableau-de-bord">
    <!-- En-tête et période -->
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
      <div>
        <h2 class="fw-bold mb-0">Bonjour {{ prenom }}</h2>
        <small class="text-muted">{{ d?.clinique || '' }} · {{ dateDuJour }}</small>
      </div>
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <div class="btn-group btn-group-sm flex-wrap" role="group" aria-label="Période">
          <button v-for="p in periodes" :key="p.cle" type="button" class="btn" :class="periode === p.cle ? 'btn-primary' : 'btn-outline-primary'" @click="choisir(p.cle)">{{ p.libelle }}</button>
        </div>
        <template v-if="periode === 'perso'">
          <input v-model="du" type="date" class="form-control form-control-sm w-auto" aria-label="Du" @change="charger">
          <input v-model="au" type="date" class="form-control form-control-sm w-auto" aria-label="Au" @change="charger">
        </template>
        <button class="btn btn-sm btn-outline-secondary" :disabled="chargement" title="Actualiser" @click="charger"><i class="bi bi-arrow-clockwise"></i></button>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>
    <div v-if="!d && chargement" class="text-center my-5"><div class="spinner-border text-primary"></div></div>

    <template v-if="d">
      <!-- À faire maintenant -->
      <h6 class="titre-section">Aujourd'hui</h6>
      <div class="row g-3 mb-4">
        <div v-if="a.consultations" class="col-sm-6 col-xl-4">
          <carte icone="bi-people" titre="Salle d'attente" :lien="lien('/salle-attente')">
            <div class="chiffre">{{ a.consultations.enAttente }} <small>en attente</small></div>
            <ul class="details">
              <li>{{ a.consultations.enCours }} en consultation · {{ a.consultations.terminees }} terminée(s)</li>
              <li :class="{ alerte: a.consultations.aPayer }">{{ a.consultations.aPayer }} en attente de paiement</li>
              <li v-if="a.mesPatients !== undefined" class="fw-semibold">{{ a.mesPatients }} patient(s) pour vous</li>
            </ul>
          </carte>
        </div>

        <div v-if="a.rendezVous" class="col-sm-6 col-xl-4">
          <carte icone="bi-calendar-check" titre="Rendez-vous du jour" :lien="lien('/rendez-vous')">
            <div class="chiffre">{{ a.rendezVous.attendus }} <small>attendu(s)</small></div>
            <ul class="details">
              <li>{{ a.rendezVous.arrives }} arrivé(s) · {{ a.rendezVous.absents }} absent(s)</li>
              <li v-for="r in a.rendezVous.prochains.slice(0, 3)" :key="r.id"><strong>{{ r.heure }}</strong> {{ r.patient }} <span class="text-muted">· {{ r.medecin }}</span></li>
            </ul>
          </carte>
        </div>

        <div v-if="a.laboratoire" class="col-sm-6 col-xl-4">
          <carte icone="bi-eyedropper" titre="Laboratoire" :lien="lien('/laboratoire')" :alerte="a.laboratoire.urgents > 0">
            <div class="chiffre">{{ a.laboratoire.aPrelever + a.laboratoire.aSaisir + a.laboratoire.aValider }} <small>examen(s) en cours</small></div>
            <ul class="details">
              <li>{{ a.laboratoire.aPrelever }} à prélever · {{ a.laboratoire.aSaisir }} à saisir · {{ a.laboratoire.aValider }} à valider</li>
              <li v-if="a.laboratoire.urgents" class="alerte"><i class="bi bi-exclamation-triangle me-1"></i>{{ a.laboratoire.urgents }} urgent(s)</li>
            </ul>
          </carte>
        </div>

        <div v-if="a.hospitalisation" class="col-sm-6 col-xl-4">
          <carte icone="bi-hospital" titre="Hospitalisation" :lien="lien('/hospitalisations')">
            <div class="chiffre">{{ a.hospitalisation.hospitalises }} <small>patient(s) hospitalisé(s)</small></div>
            <ul class="details">
              <li>{{ a.hospitalisation.lits - a.hospitalisation.litsOccupes }} lit(s) libre(s) sur {{ a.hospitalisation.lits }}</li>
              <li :class="{ alerte: a.hospitalisation.sortiesPrevues }">{{ a.hospitalisation.sortiesPrevues }} sortie(s) prévue(s) ou dépassée(s)</li>
            </ul>
            <div v-if="a.hospitalisation.lits" class="jauge" role="img" :aria-label="`Occupation ${tauxOccupation} %`">
              <div :style="{ width: tauxOccupation + '%' }"></div>
            </div>
          </carte>
        </div>

        <div v-if="a.pharmacie" class="col-sm-6 col-xl-4">
          <carte icone="bi-capsule" titre="Stock de la pharmacie" :lien="lien('/stocks-lots') || lien('/produits')" :alerte="a.pharmacie.ruptures + a.pharmacie.lotsPerimes > 0">
            <div class="chiffre">{{ a.pharmacie.ruptures }} <small>produit(s) en rupture</small></div>
            <ul class="details">
              <li :class="{ alerte: a.pharmacie.sousSeuil }">{{ a.pharmacie.sousSeuil }} sous le seuil d'alerte</li>
              <li :class="{ alerte: a.pharmacie.lotsPerimes }">{{ a.pharmacie.lotsPerimes }} lot(s) périmé(s) encore en stock</li>
              <li>{{ a.pharmacie.lotsBientotPerimes }} lot(s) périmant sous {{ a.pharmacie.joursPeremption }} jours</li>
            </ul>
          </carte>
        </div>

        <div v-if="a.assurances" class="col-sm-6 col-xl-4">
          <carte icone="bi-shield-check" titre="Assurances" :lien="lien('/creances-assurance')">
            <div class="chiffre">{{ fcfa(a.assurances.attenduDesAssureurs) }}</div>
            <ul class="details">
              <li>attendus des assureurs (bordereaux envoyés)</li>
              <li :class="{ alerte: a.assurances.nombreAEnvoyer }">{{ fcfa(a.assurances.aEnvoyer) }} à mettre sur un bordereau ({{ a.assurances.nombreAEnvoyer }})</li>
              <li v-if="a.assurances.rejetees" class="alerte">{{ a.assurances.rejetees }} créance(s) rejetée(s)</li>
            </ul>
          </carte>
        </div>
      </div>

      <!-- Sur la période -->
      <h6 class="titre-section">{{ libellePeriode }}</h6>
      <div class="row g-3 mb-4">
        <div v-for="k in indicateurs" :key="k.titre" class="col-6 col-md-4 col-xxl-2">
          <div class="indicateur h-100">
            <div class="libelle">{{ k.titre }}</div>
            <div class="valeur" :class="k.classe">{{ k.valeur }}</div>
            <div v-if="k.detail" class="detail">{{ k.detail }}</div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-lg-6">
          <div class="bloc h-100">
            <div class="bloc-titre">Consultations {{ d.courbe.parMois ? 'par mois' : 'par jour' }}</div>
            <div class="graphique"><Bar :data="graphique('consultations', '#2a78d6', 'Consultations')" :options="optionsGraphique(false)" /></div>
          </div>
        </div>
        <div v-if="argent" class="col-lg-6">
          <div class="bloc h-100">
            <div class="bloc-titre">Encaissé {{ d.courbe.parMois ? 'par mois' : 'par jour' }} (FCFA)</div>
            <div class="graphique"><Bar :data="graphique('encaisse', '#1baf7a', 'Encaissé')" :options="optionsGraphique(true)" /></div>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div v-if="argent && argent.parSource.length" class="col-lg-4">
          <div class="bloc h-100">
            <div class="bloc-titre">Encaissé par activité</div>
            <barres :lignes="argent.parSource" :format="fcfa" />
          </div>
        </div>
        <div v-if="argent && argent.parMoyen.length" class="col-lg-4">
          <div class="bloc h-100">
            <div class="bloc-titre">Encaissé par moyen de paiement</div>
            <barres :lignes="argent.parMoyen" :format="fcfa" />
          </div>
        </div>
        <div v-if="p.pharmacie && p.pharmacie.top.length" class="col-lg-4">
          <div class="bloc h-100">
            <div class="bloc-titre">Produits les plus vendus</div>
            <barres :lignes="p.pharmacie.top.map(t => ({ libelle: t.nom, montant: t.quantite }))" :format="n => n.toLocaleString('fr-FR')" />
          </div>
        </div>
      </div>

      <p v-if="argent && (argent.annulations.nombre || argent.rembourse)" class="small text-muted mt-3 mb-0">
        <i class="bi bi-info-circle me-1"></i>Sur la période : {{ argent.annulations.nombre }} encaissement(s) annulé(s) ({{ fcfa(argent.annulations.montant) }}) et {{ fcfa(argent.rembourse) }} remboursé(s), déjà déduits.
      </p>
    </template>
  </div>
</template>

<script>
import { h } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { Chart as ChartJS, Tooltip, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'vue-chartjs';
import api, { messageErreur } from '@/api';
import { visible } from '@/navigation/menu';
import { peutTravaillerHorsLigne } from '@/horsLigne/reference';

ChartJS.register(Tooltip, CategoryScale, LinearScale, BarElement);

const fcfa = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} FCFA`;

/* Carte « à faire » : titre, chiffre principal, détails, lien vers l'écran. */
const Carte = {
  props: { icone: String, titre: String, lien: String, alerte: Boolean },
  setup(props, { slots }) {
    const routeur = useRouter();
    return () => h('div', { class: ['carte-action h-100', { 'carte-alerte': props.alerte }] }, [
      h('div', { class: 'd-flex justify-content-between align-items-center mb-2' }, [
        h('span', { class: 'carte-titre' }, [h('i', { class: ['bi me-2', props.icone] }), props.titre]),
        props.lien ? h('a', { href: props.lien, class: 'small', onClick: (e) => { e.preventDefault(); routeur.push(props.lien); } }, 'Ouvrir ›') : null,
      ]),
      slots.default?.(),
    ]);
  },
};

/* Barres horizontales simples : libellé, valeur, longueur proportionnelle. */
const Barres = {
  props: { lignes: Array, format: Function },
  setup(props) {
    return () => {
      const max = Math.max(1, ...props.lignes.map(l => Math.abs(l.montant)));
      return h('ul', { class: 'barres' }, props.lignes.map(l => h('li', { key: l.libelle }, [
        h('div', { class: 'd-flex justify-content-between small' }, [h('span', l.libelle), h('strong', props.format(l.montant))]),
        h('div', { class: 'barre' }, [h('div', { class: l.montant < 0 ? 'negatif' : '', style: { width: `${Math.round(Math.abs(l.montant) / max * 100)}%` } })]),
      ])));
    };
  },
};

export default {
  name: 'TableauDeBord',
  components: { Bar, Carte, Barres },
  data() {
    return {
      d: null,
      chargement: false,
      erreur: '',
      periode: 'jour',
      du: dayjs().format('YYYY-MM-DD'),
      au: dayjs().format('YYYY-MM-DD'),
      periodes: [
        { cle: 'jour', libelle: "Aujourd'hui" },
        { cle: '7j', libelle: '7 jours' },
        { cle: 'mois', libelle: 'Ce mois' },
        { cle: 'moisDernier', libelle: 'Mois dernier' },
        { cle: 'annee', libelle: 'Cette année' },
        { cle: 'perso', libelle: 'Période…' },
      ],
    };
  },
  computed: {
    prenom() { return this.$store.state.user?.prenom || ''; },
    dateDuJour() { return new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); },
    a() { return this.d?.aujourdhui || {}; },
    p() { return this.d?.periodeData || {}; },
    argent() { return this.p.argent || null; },
    tauxOccupation() {
      const h = this.a.hospitalisation;
      return h && h.lits ? Math.round(h.litsOccupes / h.lits * 100) : 0;
    },
    libellePeriode() {
      if (!this.d) return '';
      const { du, au } = this.d.periode;
      const f = (x) => dayjs(x).format('DD/MM/YYYY');
      return du === au ? `Activité du ${f(du)}` : `Activité du ${f(du)} au ${f(au)}`;
    },
    indicateurs() {
      const p = this.p;
      const r = [];
      if (p.activite) {
        r.push({ titre: 'Consultations', valeur: p.activite.consultations, detail: `${p.activite.terminees} terminée(s)${p.activite.annulees ? ` · ${p.activite.annulees} annulée(s)` : ''}` });
        r.push({ titre: 'Nouveaux patients', valeur: p.activite.nouveauxPatients });
      }
      if (p.argent) {
        r.push({ titre: 'Encaissé', valeur: fcfa(p.argent.encaisse), detail: 'part payée par les patients' });
        if (p.argent.partAssurance !== null && p.argent.partAssurance !== undefined) r.push({ titre: 'Part des assurances', valeur: fcfa(p.argent.partAssurance), detail: 'actes de la période, à réclamer' });
        if (p.argent.depenses !== undefined) {
          r.push({ titre: 'Dépenses', valeur: fcfa(p.argent.depenses) });
          r.push({ titre: 'Solde de caisse', valeur: fcfa(p.argent.solde), classe: p.argent.solde < 0 ? 'text-danger' : 'text-success' });
        }
      }
      if (p.pharmacie) r.push({ titre: 'Ventes pharmacie', valeur: fcfa(p.pharmacie.montant), detail: `${p.pharmacie.ventes} vente(s)` });
      if (p.hospitalisation) r.push({ titre: 'Hospitalisations', valeur: p.hospitalisation.entrees, detail: `entrée(s) · ${p.hospitalisation.sorties} sortie(s)` });
      if (p.laboratoire) r.push({ titre: 'Examens de labo', valeur: p.laboratoire.demandes, detail: `demandé(s) · ${p.laboratoire.valides} validé(s)` });
      return r;
    },
  },
  created() {
    this.charger();
  },
  methods: {
    fcfa,
    choisir(cle) {
      this.periode = cle;
      const j = dayjs();
      const bornes = {
        jour: [j, j],
        '7j': [j.subtract(6, 'day'), j],
        mois: [j.startOf('month'), j],
        moisDernier: [j.subtract(1, 'month').startOf('month'), j.subtract(1, 'month').endOf('month')],
        annee: [j.startOf('year'), j],
      }[cle];
      if (bornes) {
        this.du = bornes[0].format('YYYY-MM-DD');
        this.au = bornes[1].format('YYYY-MM-DD');
        this.charger();
      }
    },
    async charger() {
      this.chargement = true;
      this.erreur = '';
      try {
        this.d = (await api.custom(`tableau-de-bord?du=${this.du}&au=${this.au}`, 'GET')).data;
      } catch (e) {
        this.erreur = messageErreur(e, 'Impossible de charger le tableau de bord.');
      } finally {
        this.chargement = false;
      }
    },
    /** Adresse de l'écran si l'utilisateur peut l'ouvrir, sinon rien (pas de lien). */
    lien(to) {
      return visible({ to }, { router: this.$router, getters: this.$store.getters, horsLigne: peutTravaillerHorsLigne }) ? to : null;
    },
    graphique(cle, couleur, libelle) {
      const c = this.d.courbe;
      return {
        labels: c.dates.map(x => (c.parMois ? dayjs(x + '-01').format('MMM YY') : dayjs(x).format('DD/MM'))),
        datasets: [{ label: libelle, data: c[cle] || [], backgroundColor: couleur, borderRadius: 4, maxBarThickness: 28 }],
      };
    },
    optionsGraphique(argent) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => (argent ? fcfa(ctx.parsed.y) : `${ctx.parsed.y} consultation(s)`) } },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#52514e', maxRotation: 0, autoSkip: true } },
          y: { beginAtZero: true, grid: { color: '#ecebe8' }, border: { display: false }, ticks: { color: '#52514e', precision: 0, callback: (v) => (argent ? Number(v).toLocaleString('fr-FR') : v) } },
        },
      };
    },
  },
};
</script>

<style scoped>
.titre-section { text-transform: uppercase; letter-spacing: .06em; font-size: .75rem; color: #6c757d; font-weight: 700; margin-bottom: .75rem; }
:deep(.carte-action) { background: #fff; border: 1px solid #e6eaf0; border-left: 4px solid #2a78d6; border-radius: 10px; padding: 14px 16px; }
:deep(.carte-alerte) { border-left-color: #d03b3b; }
:deep(.carte-titre) { font-weight: 600; color: #1f2d3d; }
.chiffre { font-size: 1.8rem; font-weight: 700; line-height: 1.1; color: #0b0b0b; }
.chiffre small { font-size: .85rem; font-weight: 500; color: #52514e; }
.details { list-style: none; padding: 0; margin: 8px 0 0; font-size: .85rem; color: #52514e; }
.details li { padding: 2px 0; }
.details li.alerte, .alerte { color: #b42318; font-weight: 600; }
.jauge { height: 6px; background: #ecebe8; border-radius: 4px; margin-top: 10px; overflow: hidden; }
.jauge div { height: 100%; background: #2a78d6; border-radius: 4px; }
.indicateur { background: #fff; border: 1px solid #e6eaf0; border-radius: 10px; padding: 12px 14px; }
.indicateur .libelle { font-size: .8rem; color: #52514e; }
.indicateur .valeur { font-size: 1.25rem; font-weight: 700; color: #0b0b0b; line-height: 1.2; overflow-wrap: anywhere; }
.indicateur .detail { font-size: .75rem; color: #6c757d; }
.bloc { background: #fff; border: 1px solid #e6eaf0; border-radius: 10px; padding: 14px 16px; }
.bloc-titre { font-weight: 600; margin-bottom: 10px; color: #1f2d3d; }
.graphique { height: 240px; }
:deep(.barres) { list-style: none; padding: 0; margin: 0; }
:deep(.barres li) { margin-bottom: 10px; }
:deep(.barre) { height: 8px; background: #f1f0ed; border-radius: 4px; overflow: hidden; margin-top: 3px; }
:deep(.barre div) { height: 100%; background: #2a78d6; border-radius: 4px; }
:deep(.barre div.negatif) { background: #d03b3b; }
</style>
